import { useState } from "react";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import { RenderWhen } from "../../../components";
import YesNoModal from "../../../components/YesNoModal";
import InputCheckbox from "../../../forms/components/InputCheckbox";

const SHOW_TICLOUD_ENABLE = window?.tcbase?.consts?.cloud_enabled;

type Props = {
  canUserPublishToSdm: boolean;
  isPublishedToSdm: boolean;
  isCoreRuleset: boolean;
  isParanoidRuleset: boolean;
  enableRuleset: boolean;
  enableRulesetTiCloud: boolean;
  enableAutoCloudRetro: boolean;
  saveEnabled: boolean;
  onEnableRulesetClick: (value: boolean) => void;
  onEnableRulesetTiCloudClick: (value: boolean) => void;
  onSaveClick: () => void;
  onSaveCloseClick: () => void;
};

const YaraHeaderControls = ({
  canUserPublishToSdm,
  isPublishedToSdm,
  isCoreRuleset, // core rulesets cannot be saved!
  isParanoidRuleset, // paranoid mode ruleset cannot be saved or modified
  enableRuleset,
  enableRulesetTiCloud,
  enableAutoCloudRetro,
  saveEnabled,
  onEnableRulesetClick,
  onEnableRulesetTiCloudClick,
  onSaveClick,
  onSaveCloseClick
}: Props) => {
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const hidePublishConfirm = () => setShowPublishConfirm(false);

  const canBeSaved = !(isCoreRuleset || isParanoidRuleset);

  return (
    <div className={"yara-editor-header"}>
      <RenderWhen
        condition={canBeSaved}
        fallback={
          <span>
            {isCoreRuleset ? "System" : "Risk Tolerance"} Rulesets Cannot Be
            Saved.
          </span>
        }
      >
        <>
          <div className={"yara-editor-switches"}>
            <label>
              <InputCheckbox
                value={enableRuleset}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onEnableRulesetClick(e.target.checked)
                }
                data-testid="yara_enable_ruleset_checkbox"
              />
              <span className={"yara-editor-label"}>Enable Ruleset</span>
            </label>
            {SHOW_TICLOUD_ENABLE && (
              <label>
                <InputCheckbox
                  value={enableRulesetTiCloud}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onEnableRulesetTiCloudClick(e.target.checked)
                  }
                />
                <span className={"yara-editor-label"}>
                  Run Ruleset Continuously in Spectra Intelligence{" "}
                  {enableAutoCloudRetro
                    ? "(a YARA Retro Scan Will Automatically Start if Cloud-Enabled.)"
                    : ""}
                </span>
              </label>
            )}
          </div>

          <ButtonToolbar style={{ marginBottom: "3px" }}>
            <RenderWhen condition={!canUserPublishToSdm || !isPublishedToSdm}>
              <Button
                disabled={!saveEnabled}
                onClick={() => onSaveClick()}
                data-testid="yara_save_button"
              >
                Save
              </Button>
            </RenderWhen>
            <Button
              disabled={!saveEnabled}
              onClick={() => {
                if (canUserPublishToSdm) {
                  setShowPublishConfirm(true);
                  return;
                }
                onSaveCloseClick();
              }}
              data-testid="yara_save_and_publish_or_save_and_close"
            >
              {canUserPublishToSdm ? "Save & Publish" : "Save & Close"}
            </Button>
          </ButtonToolbar>
          <YesNoModal
            show={showPublishConfirm}
            title="Synchronize Ruleset"
            onClickNo={hidePublishConfirm}
            onClickYes={onSaveCloseClick}
            size="sm"
          >
            Do you want to synchronize the ruleset across other appliances?
          </YesNoModal>
        </>
      </RenderWhen>
    </div>
  );
};

export default YaraHeaderControls;
